<template>
  <div class="event-modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" @click.self="$emit('close')">
    <div class="event-modal bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {{ isEditing ? 'Editar evento' : 'Novo evento' }}
        </h2>
        <button class="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" @click="$emit('close')">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Form -->
      <form class="p-6 space-y-5" @submit.prevent="handleSubmit">
        <!-- Title -->
        <div>
          <input
            v-model="form.summary"
            type="text"
            placeholder="Adicionar título"
            class="w-full text-xl font-medium bg-transparent border-0 border-b-2 border-slate-200 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400 outline-none pb-2 text-slate-800 dark:text-slate-100 placeholder-slate-400"
          />
        </div>

        <!-- All day toggle -->
        <label class="flex items-center gap-3 cursor-pointer">
          <input v-model="form.allDay" type="checkbox" class="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
          <span class="text-sm text-slate-700 dark:text-slate-300">Dia inteiro</span>
        </label>

        <!-- Date/Time -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Início</label>
            <input
              v-if="form.allDay"
              v-model="form.startDate"
              type="date"
              class="event-input"
            />
            <input
              v-else
              v-model="form.startTime"
              type="datetime-local"
              class="event-input"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Término</label>
            <input
              v-if="form.allDay"
              v-model="form.endDate"
              type="date"
              class="event-input"
            />
            <input
              v-else
              v-model="form.endTime"
              type="datetime-local"
              class="event-input"
            />
          </div>
        </div>

        <!-- Google Meet -->
        <label class="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
          <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-blue-600 dark:text-blue-400"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
          </div>
          <div class="flex-1">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">Google Meet</span>
            <p class="text-xs text-slate-500 dark:text-slate-400">Adicionar videoconferência automaticamente</p>
          </div>
          <input v-model="form.addMeet" type="checkbox" class="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
        </label>

        <!-- Attendees -->
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Convidados</label>
          <div class="space-y-2">
            <div v-for="(attendee, idx) in form.attendees" :key="idx" class="flex items-center gap-2">
              <input
                v-model="attendee.email"
                type="email"
                placeholder="email@exemplo.com"
                class="event-input flex-1"
              />
              <input
                v-model="attendee.name"
                type="text"
                placeholder="Nome (opcional)"
                class="event-input flex-1"
              />
              <button type="button" class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors" @click="removeAttendee(idx)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <button type="button" class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium" @click="addAttendee">
              + Adicionar convidado
            </button>
          </div>
        </div>

        <!-- Location -->
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Local</label>
          <input
            v-model="form.location"
            type="text"
            placeholder="Adicionar local ou endereço"
            class="event-input"
          />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Descrição</label>
          <textarea
            v-model="form.description"
            placeholder="Adicionar descrição ou notas"
            rows="3"
            class="event-input resize-none"
          />
        </div>

        <!-- Color -->
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Cor</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="color in googleColors"
              :key="color.id"
              type="button"
              class="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
              :class="{ 'ring-2 ring-offset-2 ring-blue-500 scale-110': form.colorId === color.id }"
              :style="{ backgroundColor: color.hex, borderColor: form.colorId === color.id ? color.hex : 'transparent' }"
              :title="color.name"
              @click="form.colorId = color.id"
            />
          </div>
        </div>

        <!-- Status (Busy/Free) -->
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Status</label>
          <select v-model="form.transparency" class="event-input">
            <option value="opaque">Ocupado</option>
            <option value="transparent">Livre</option>
          </select>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
          <button
            v-if="isEditing"
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            @click="handleDelete"
          >
            Excluir evento
          </button>
          <div v-else />
          <div class="flex gap-2">
            <button type="button" class="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" @click="$emit('close')">
              Cancelar
            </button>
            <button
              type="submit"
              class="px-6 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              :disabled="!form.summary || isSaving"
            >
              {{ isSaving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';

const props = defineProps({
  event: { type: Object, default: null },
  funnelId: { type: [Number, String], default: null },
  isEditing: { type: Boolean, default: false },
});

const emit = defineEmits(['save', 'delete', 'close']);

const isSaving = ref(false);

// Google Calendar event colors
const googleColors = [
  { id: '1', name: 'Lavanda', hex: '#7986cb' },
  { id: '2', name: 'Sálvia', hex: '#33b679' },
  { id: '3', name: 'Uva', hex: '#8e24aa' },
  { id: '4', name: 'Flamingo', hex: '#e67c73' },
  { id: '5', name: 'Banana', hex: '#f6bf26' },
  { id: '6', name: 'Tangerina', hex: '#f4511e' },
  { id: '7', name: 'Pavão', hex: '#039be5' },
  { id: '8', name: 'Grafite', hex: '#616161' },
  { id: '9', name: 'Mirtilo', hex: '#3f51b5' },
  { id: '10', name: 'Manjericão', hex: '#0b8043' },
  { id: '11', name: 'Tomate', hex: '#d50000' },
];

const getDefaultStart = () => {
  if (props.event?.start_date) {
    const d = new Date(props.event.start_date);
    return d.toISOString().slice(0, 16);
  }
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 1);
  return now.toISOString().slice(0, 16);
};

const getDefaultEnd = () => {
  if (props.event?.start_date) {
    const d = new Date(props.event.start_date);
    d.setHours(d.getHours() + 1);
    return d.toISOString().slice(0, 16);
  }
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 2);
  return now.toISOString().slice(0, 16);
};

const form = reactive({
  summary: '',
  description: '',
  location: '',
  allDay: false,
  startTime: getDefaultStart(),
  endTime: getDefaultEnd(),
  startDate: new Date().toISOString().slice(0, 10),
  endDate: new Date().toISOString().slice(0, 10),
  addMeet: false,
  attendees: [],
  colorId: null,
  transparency: 'opaque',
});

// Populate form when editing
onMounted(() => {
  if (props.event && props.isEditing) {
    form.summary = props.event.summary || '';
    form.description = props.event.description || '';
    form.location = props.event.location || '';
    form.allDay = props.event.is_all_day || false;
    form.addMeet = props.event.has_meet || false;
    form.colorId = props.event.color_id || null;
    form.transparency = props.event.transparency || 'opaque';

    if (props.event.is_all_day) {
      form.startDate = props.event.start || new Date().toISOString().slice(0, 10);
      form.endDate = props.event.end || form.startDate;
    } else {
      const start = props.event.start ? new Date(props.event.start).toISOString().slice(0, 16) : getDefaultStart();
      const end = props.event.end ? new Date(props.event.end).toISOString().slice(0, 16) : getDefaultEnd();
      form.startTime = start;
      form.endTime = end;
    }

    if (props.event.attendees?.length) {
      form.attendees = props.event.attendees
        .filter(a => !a.self)
        .map(a => ({ email: a.email, name: a.name || '' }));
    }
  }
});

const addAttendee = () => {
  form.attendees.push({ email: '', name: '' });
};

const removeAttendee = (idx) => {
  form.attendees.splice(idx, 1);
};

const handleSubmit = () => {
  if (!form.summary) return;
  isSaving.value = true;

  const payload = {
    summary: form.summary,
    description: form.description,
    location: form.location,
    add_meet: form.addMeet,
    color_id: form.colorId,
    transparency: form.transparency,
    all_day: form.allDay,
    attendees: form.attendees.filter(a => a.email),
  };

  if (form.allDay) {
    payload.start_date = form.startDate;
    payload.end_date = form.endDate;
  } else {
    // Guard against invalid dates
    const startDt = new Date(form.startTime);
    const endDt = new Date(form.endTime);
    if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime())) {
      isSaving.value = false;
      return;
    }
    payload.start_time = startDt.toISOString();
    payload.end_time = endDt.toISOString();
  }

  emit('save', payload);
  // Reset isSaving after emit (parent handles async)
  setTimeout(() => { isSaving.value = false; }, 500);
};

const handleDelete = () => {
  if (props.event?.id && window.confirm('Tem certeza que deseja excluir este evento?')) {
    emit('delete', props.event.id);
  }
};
</script>

<style scoped>
.event-modal-overlay {
  animation: fadeIn 0.15s ease-out;
}

.event-modal {
  animation: slideUp 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.event-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #1e293b;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.event-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}

:root[data-theme="dark"] .event-input,
.dark .event-input {
  border-color: #475569;
  background-color: #334155;
  color: #f1f5f9;
}

:root[data-theme="dark"] .event-input:focus,
.dark .event-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px #60a5fa;
}
</style>
